'use client';

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import posthog from 'posthog-js';

import { resetAnalyticsIdentity } from '@/lib/analytics';
import { DisplayUser } from '@/lib/api';
import { getCurrentUser } from '@/lib/api/generated';

/** Limits how long the user-info authentication probe can remain pending. */
const USER_INFO_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Atomic authentication snapshot published by `UserInfoProvider`.
 *
 * `loading` and `error` retain the last confirmed user when one exists. `signed_out` is reserved
 * for a confirmed HTTP 401 response, while `authenticated` contains the latest confirmed user.
 */
export type UserInfoState =
  | { status: 'loading'; userInfo: DisplayUser | null }
  | { status: 'authenticated'; userInfo: DisplayUser }
  | { status: 'signed_out'; userInfo: null }
  | { status: 'error'; userInfo: DisplayUser | null };

/** Public authentication state and refresh operations available to context consumers. */
type UserInfoContextType = {
  /** Complete authentication state for consumers that distinguish confirmed sign-out. */
  userInfoState: UserInfoState;
  /** Last confirmed user, retained while refreshes are loading or fail unexpectedly. */
  userInfo: DisplayUser | null;
  /** Compatibility projection that is true exactly while the atomic state is `loading`. */
  isUserInfoLoading: boolean;
  /** Rechecks authentication and resolves when the fetch settles. */
  reloadUserInfo: () => Promise<void>;
  /** Rechecks authentication and returns the parsed user. */
  fetchUserInfo: () => Promise<DisplayUser | null>;
};

/** Undefined outside the provider so misuse fails explicitly in `useUserInfo`. */
const UserInfoContext = createContext<UserInfoContextType | undefined>(undefined);

/** Children whose authentication consumers share one provider snapshot. */
type UserInfoProviderProps = {
  children: React.ReactNode;
};

/** Owns the atomic user-info request state. */
export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({ children }) => {
  /** Sole mutable authentication snapshot exposed by this provider. */
  const [userInfoState, setUserInfoState] = useState<UserInfoState>({
    status: 'loading',
    userInfo: null,
  });
  /**
   * Generation counter that orders overlapping `/user/info` probes.
   *
   * Probes from mount, `reloadUserInfo`, and the login poll can resolve in any order; a
   * superseded one must not overwrite the current identity.
   */
  const latestProbe = useRef(0);

  /**
   * Drops the PostHog identity, returning this browser to an anonymous `distinct_id`.
   *
   * Only for a real sign-out: resetting mints a new anonymous id, so doing it on a failed probe
   * would split one person into two identities. Asks PostHog whether the browser is identified,
   * since that survives reloads and component state doesn't.
   */
  const clearAnalyticsIdentity = useCallback(() => {
    if (posthog._isIdentified()) {
      resetAnalyticsIdentity();
    }
  }, []);

  /**
   * Fetches the current user's profile details for provider consumers.
   *
   * Returns a `Promise<DisplayUser | null>` that resolves to the authenticated user when the
   * protected `/user/info` probe succeeds. Treats HTTP 401 as the normal unauthenticated state,
   * clears the cached user and the analytics identity, and returns `null` without logging an
   * error. Logs unexpected non-OK responses, network failures, and JSON failures while retaining
   * the last confirmed user and identity: they aren't sign-outs.
   *
   * Once a newer probe supersedes it (see `latestProbe`), a probe returns its result but stops
   * writing shared state.
   */
  const fetchUserInfo = useCallback(async (): Promise<DisplayUser | null> => {
    // Claimed before the first await, so a later probe always takes a higher generation.
    const probe = (latestProbe.current += 1);
    /** True while no later probe has started; gates every write to shared state below. */
    const isCurrentProbe = (): boolean => latestProbe.current === probe;
    setUserInfoState((state) => ({ status: 'loading', userInfo: state.userInfo }));
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), USER_INFO_REQUEST_TIMEOUT_MS);

    try {
      const userDetails = await getCurrentUser({
        signal: controller.signal,
      });
      if (!isCurrentProbe()) {
        return userDetails;
      }
      // `public_id` is stable across username and email changes, and server events use it too.
      const distinctId = userDetails.public_id.trim();
      // Compared with PostHog's persisted id, so a reload does no work.
      if (posthog.get_distinct_id() !== distinctId) {
        // Another account's identity is dropped so the two don't merge; an anonymous browser's
        // history is kept and merged in.
        if (posthog._isIdentified()) {
          resetAnalyticsIdentity();
        }
        // `public_id` alone: anything passed here becomes a person property, and analytics
        // carries no personal data.
        posthog.identify(distinctId);
      }
      setUserInfoState({ status: 'authenticated', userInfo: userDetails });
      return userDetails;
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 401
      ) {
        // Treats the protected auth probe's unauthenticated response as the normal signed-out state.
        if (isCurrentProbe()) {
          clearAnalyticsIdentity();
          setUserInfoState({ status: 'signed_out', userInfo: null });
        }
        return null;
      }
      // Logged even when superseded: a log isn't shared state.
      console.error('Error reloading user details:', error);
      if (isCurrentProbe()) {
        setUserInfoState((state) => ({ status: 'error', userInfo: state.userInfo }));
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }, [clearAnalyticsIdentity]);

  /** Repeats the auth probe while intentionally hiding its user-returning fetch contract. */
  const reloadUserInfo = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  // Fetches user info on mount, including the expected unauthenticated probe result.
  React.useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  // These compatibility projections are derived from, and cannot diverge from, the snapshot.
  const userInfo = userInfoState.userInfo;
  const isUserInfoLoading = userInfoState.status === 'loading';

  return (
    <UserInfoContext.Provider
      value={{ userInfoState, userInfo, isUserInfoLoading, reloadUserInfo, fetchUserInfo }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

/** Reads the complete auth contract and rejects consumers mounted outside its provider. */
export const useUserInfo = (): UserInfoContextType => {
  const context = useContext(UserInfoContext);
  if (context === undefined) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }
  return context;
};
